import React, { useEffect, useRef, useState } from 'react';
import Flickity from 'flickity';
import 'flickity/css/flickity.css';
import { jwtDecode } from 'jwt-decode';
import api from '../services/api';
import { showToast } from '../context/ToastHandle';

const individualPlans = [
  { key: 'free', label: 'Free Trial', price: '£0', features: ['21 days access', 'Limited features'] },
  { key: 'basic', label: 'Basic', price: '£2', features: ['5 boards', 'Upload custom images'] },
  { key: 'premium', label: 'Premium', price: '£4', features: ['Basic +', '40 boards', 'Offline mode'] },
  { key: 'unlimited', label: 'Unlimited', price: '£6', features: ['Premium +', '1000 boards', 'Folders', 'Sticky Core Boards', '500+ Preset Boards'] },
];

const organisationPlans = [
  { key: 'basic', label: 'Basic Org', price: '£3', features: ['1 user', '5 boards', 'Upload custom images'] },
  { key: 'premium', label: 'Premium Org', price: '£10', features: ['Up to 4 users', 'Basic +', '40 boards', 'Offline mode'] },
  { key: 'unlimited', label: 'Unlimited Org', price: '£30', features: ['Up to 11 users', 'Premium +', '1000 boards', 'Folders', 'Sticky Core Boards', '500+ Preset Boards'] },
];

const priceMap = {
  individual: {
    basic: 'price_1U3M9XJqOfSFInNNFgfev0sZ',
    premium: 'price_1U3MAtJqOfSFInNNm2rf5bZY',
    unlimited: 'price_1U3MBhJqOfSFInNNpkgzQZIq'
  },
  organisation: {
    basic: 'price_1SE9HOJqOfSFInNNrOz0HGdJ',
    premium: 'price_1SE9INJqOfSFInNN5umVGvcC',
    unlimited: 'price_1SE9IxJqOfSFInNNC5zlxLeJ'
  }
};

const PlanSelector = () => {
  const [userType, setUserType] = useState('individual');
  const [checkOrg, setCheckOrg] = useState(false);
  const [orgOwner, setOrgOwner] = useState(false);
  const [currentPlan, setCurrentPlan] = useState('free');
  const [plans, setPlans] = useState([]);
  const carouselRef = useRef(null);
  const flickityInstance = useRef(null);

  useEffect(() => {

    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const decoded = jwtDecode(token);

      setCurrentPlan(decoded.acc_type || 'free');
      const isOrgOwner = decoded.org_owner === true || decoded.org_owner === 'true' || decoded.org_owner === 1;
      setOrgOwner(isOrgOwner);
      const type = decoded.user_type || 'individual';
      setUserType(type);
      const planSet = type === 'organisation' ? organisationPlans : individualPlans;
      setPlans(planSet);
      setCheckOrg(!isOrgOwner && type === 'organisation');

      setTimeout(() => {
        if (carouselRef.current && !flickityInstance.current) {
          flickityInstance.current = new Flickity(carouselRef.current, {
            cellAlign: 'center',
            freeScroll: false,
            wrapAround: true,
            prevNextButtons: false,
            pageDots: false
          });

          const index = planSet.findIndex(p => p.key === decoded.acc_type);
          if (index >= 0) {
            flickityInstance.current.select(index);
          }
        }
      }, 50);
    } catch (err) {
      console.error('Token decode failed:', err);
    }

    return () => {
      flickityInstance.current?.destroy();
    };
  }, []);

  const getPlanOrder = () =>
    userType === 'organisation'
      ? ['basic', 'premium', 'unlimited']
      : ['free', 'basic', 'premium', 'unlimited'];

  const handleManageSubscription = async () => {
    const token = localStorage.getItem('token');
    if(checkOrg){
      showToast('Only the organisation owner can manage the subscription. Please contact them.', 'error', false);
      return;
    }
    try {
      const res = await api.post('/create-customer-portal-session.php', { token });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        showToast('Stripe URL not returned.', 'error', false);
      }
    } catch (err) {
      console.error('Stripe portal error:', err);
      showToast('Could not open Stripe Portal.', 'error', false);
    }
  };

  const handlePlanSelect = async (planKey) => {
    const planOrder = getPlanOrder();
    const currentIndex = planOrder.indexOf(currentPlan);
    const newIndex = planOrder.indexOf(planKey);

    if(checkOrg){
      showToast('Only the organisation owner can manage the subscription. Please contact them.', 'error', false);
      return;
    }

    if (newIndex <= currentIndex) {
      showToast('Please use Manage Subscription to downgrade or change.', 'info', false);
      return;
    }

    const priceId = priceMap[userType]?.[planKey];
    if (!priceId) {
      showToast('Price ID not found for this plan.', 'error', false);
      return;
    }

    try {
      const res = await api.post('/create-checkout-session.php', {
        price_id: priceId,
        token: localStorage.getItem('token')
      });

      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        showToast('Stripe URL not returned.', 'error', false);
      }
    } catch (err) {
      console.error('Stripe checkout error:', err);
      showToast('Checkout failed.', 'error', false);
    }
  };

  const buttonStyle = {
    marginTop: '1rem',
    backgroundColor: '#1976d2',
    color: '#fff',
    border: 'none',
    padding: '0.5rem 1rem',
    borderRadius: '5px',
    cursor: 'pointer'
  };

  return (
    <div className="carousel" ref={carouselRef}>
      {plans.map(plan => {
        const planOrder = getPlanOrder();
        const isCurrent = plan.key === currentPlan;
        const currentIndex = planOrder.indexOf(currentPlan);
        const planIndex = planOrder.indexOf(plan.key);
        const isUpgrade = planIndex > currentIndex;

        return (
          <div
            key={plan.key}
            className="carousel-cell"
            style={{
              width: '50%',
              margin: '0 10px',
              border: isCurrent ? '2px solid #1976d2' : '1px solid #ccc',
              borderRadius: '10px',
              padding: '1rem',
              background: isCurrent ? '#f0f8ff' : '#fff',
              boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
              textAlign: 'center',
            }}
          >
            <h3>{plan.label}</h3>
            <p style={{ fontSize: '1.2rem', margin: '0.5rem 0' }}>{plan.price} / month</p>
            <ul style={{ paddingLeft: '1.2rem', textAlign: 'left' }}>
              {plan.features.map((f, i) => <li key={i}>{f}</li>)}
            </ul>

            {isCurrent && plan.key !== 'free' && (
              <button style={buttonStyle} onClick={handleManageSubscription}>
                Manage Subscription
              </button>
            )}

            {!isCurrent && isUpgrade && plan.key !== 'free' && (
              <button style={buttonStyle} onClick={() => handlePlanSelect(plan.key)}>
                Select Plan
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default PlanSelector;
